import React, { useState, useEffect } from "react";
import { sendMessage, registerTradeMatchHandler } from "../services/websocket";
import ToastContainer from "./Toast";
import "./TradeForm.css";

const TradeForm = ({ wsConnection, symbol, portfolio }) => {
  const [side, setSide] = useState("buy");
  const [price, setPrice] = useState("");
  const [quantity, setQuantity] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    // Register handler for trade matches
    const unregisterTradeMatch = registerTradeMatchHandler((trade) => {
      const toastMessage = `Your ${trade.side.toUpperCase()} order matched: ${
        trade.quantity
      } ${trade.symbol} @ $${trade.price}`;
      addToast(toastMessage, "success");
    });

    // Clean up on component unmount
    return () => {
      unregisterTradeMatch();
    };
  }, []);

  // Add toast to the stack
  const addToast = (message, type = "info", duration = 5000) => {
    const id = Date.now();
    setToasts((prevToasts) => [...prevToasts, { id, message, type, duration }]);
  };

  // Remove toast from the stack
  const removeToast = (id) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      // Validate inputs
      if (!price || !quantity) {
        throw new Error("Please enter both price and quantity");
      }

      const priceNum = parseFloat(price);
      const quantityNum = parseFloat(quantity);

      if (isNaN(priceNum) || isNaN(quantityNum)) {
        throw new Error("Please enter valid numbers");
      }

      if (priceNum <= 0 || quantityNum <= 0) {
        throw new Error("Price and quantity must be greater than 0");
      }

      // Validate integers
      if (!Number.isInteger(priceNum)) {
        throw new Error("Price must be an integer");
      }

      if (!Number.isInteger(quantityNum)) {
        throw new Error("Quantity must be an integer");
      }

      // Validate price range based on symbol
      if (priceNum > 100000) {
        throw new Error("Price must be between 1 and 100000");
      }

      // Check if user has enough funds for buy orders (sell orders allow short selling)
      if (side === "buy") {
        const totalCost = priceNum * quantityNum;
        if (totalCost > portfolio.cash.unlocked) {
          throw new Error("Insufficient funds for this trade");
        }
      }
      // No position check for sell orders - short selling is allowed

      // Prepare the order payload
      const orderPayload = {
        command: side === "buy" ? "buy_request" : "sell_request",
        order: {
          symbol: symbol,
          price: priceNum,
          quantity: quantityNum,
          timestamp: Math.floor(Date.now() / 1000),
          side,
        },
      };

      // Send the order
      const success = sendMessage(orderPayload);
      if (!success) {
        throw new Error("Failed to send order. Please try again.");
      }

      // Clear form on success
      setPrice("");
      setQuantity("");
      setError("");
      setNotification({
        type: "success",
        message: "Order submitted successfully!",
      });

      // Clear notification after 5 seconds
      setTimeout(() => {
        setNotification(null);
      }, 5000);
    } catch (err) {
      setError(err.message);
      setNotification({
        type: "error",
        message: `Failed to submit order: ${err.message}`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSideChange = (newSide) => {
    setSide(newSide);
    setError("");
  };

  return (
    <div className="trade-form">
      <div className="trade-form-header">
        <h2>Place Order <span className="trade-form-symbol">{symbol}</span></h2>
        <div className="trade-type-selector">
          <button
            className={`trade-type-button ${
              side === "buy" ? "active-buy" : ""
            }`}
            onClick={() => handleSideChange("buy")}>
            Buy
          </button>
          <button
            className={`trade-type-button ${
              side === "sell" ? "active-sell" : ""
            }`}
            onClick={() => handleSideChange("sell")}>
            Sell
          </button>
        </div>
      </div>

      {notification && (
        <div className={`notification ${notification.type}`}>
          {notification.type === "success" && <span className="icon">✓</span>}
          {notification.type === "error" && <span className="icon">✗</span>}
          {notification.message}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="price">Price (USD)</label>
          <input
            type="number"
            id="price"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="Enter price"
            step="1"
            min="1"
            max={100000}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="quantity">Quantity</label>
          <input
            type="number"
            id="quantity"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            placeholder="Enter quantity"
            step="1"
            min="1"
            required
          />
        </div>

        {error && <div className="error-message">{error}</div>}

        <button
          type="submit"
          className={`submit-button ${
            side === "buy" ? "active-buy" : "active-sell"
          }`}
          disabled={isSubmitting}>
          {isSubmitting
            ? "Submitting..."
            : `${side === "buy" ? "Buy" : "Sell"} ${symbol}`}
        </button>
      </form>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default TradeForm;
